

























import { Vue, Component, InjectReactive, Prop } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import ConversationUserPermissionsModel from '@/models/crm/conversation-user-permissions.model';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import ConversationService from '@/services/crm/conversation.service';
import { ConversationContactStatusEnum } from '@/enums/crm/conversation-contact-status.enum';
import ContactModel from '@/models/crm/contact.model';
import { ICreateConversation } from '@/interfaces/crm/create-conversation.interface';
import SettingsService from '@/services/crm/settings.service';
import SettingsModel from '@/models/crm/settings.model';
import SessionService from '@/services/session.service';
import UserContactInfo from '@/models/crm/user-contact-info.model';

@Component({})
export default class CrmChatCallerButton extends Vue {
  @inject(InjectionIdEnum.CrmConversationService)
  private conversationService!: ConversationService;

  @inject(InjectionIdEnum.CrmSettingsService)
  private settingsService!: SettingsService;

  @inject(InjectionIdEnum.SessionService)
  private sessionService!: SessionService;

  @InjectReactive('conversationUserPermission') readonly conversationUserPermission!: ConversationUserPermissionsModel;

  @InjectReactive('userContactInfo') readonly userContactInfo!: UserContactInfo;

  @Prop({ required: true })
  contactNumber!: string;

  @Prop()
  clientId!: string;

  @Prop()
  contact!: ContactModel;

  @Prop()
  width!: boolean | string;

  @Prop()
  height!: boolean | string;

  @Prop()
  color!: string;

  @Prop({ default: false })
  disabled!: boolean;

  @Prop()
  nameButton!: string;

  @Prop({ default: true })
  fab!: boolean;

  @Prop({ default: 'crm.chatCallerButton.messages.InactiveContact' })
  tooltipDisabled!: string;

  loading = false;

  private settings!: SettingsModel;

  async mounted(): Promise<void> {
    this.settings = await this.settingsService.getSettings();
  }

  async onClick(): Promise<void> {
    this.loading = true;
    try {
      if (this.settings.flagUtilizaEvoTalks && this.settings.urlBaseEvoTalks) {
        const normalizedNumber = this.normalizeContactNumber();
        const clipboardNumber = this.getClipboardContactNumber();
        const evoTalksUrl = new URL('/base/agentchat', this.settings.urlBaseEvoTalks);
        evoTalksUrl.searchParams.set('n', normalizedNumber);
        await navigator.clipboard.writeText(clipboardNumber);
        this.$notify.success(this.$t('crm.chatCallerButton.messages.numberCopiedToClipboard'));
        window.open(evoTalksUrl.toString(), 'janelaCompartilhada');
        this.loading = false;
        return;
      }

      if (this.canOpenConversation()) {
        const normalizedNumber = this.normalizeContactNumber();
        const checkResult = await this.conversationService.checkContactNumber(normalizedNumber);
        const validWaContact = checkResult.find((x) => x.status === ConversationContactStatusEnum.Valid);

        if (validWaContact) {
          const payload: ICreateConversation = {
            contact: this.contact,
            waContact: validWaContact,
          };
          const { apiToken, apiUrl, socketIOUrl } = this.sessionService;
          this.$store.commit('setContactNumber', normalizedNumber);
          this.$store.commit('setClientId', this.clientId);
          this.$store.commit('setCreateConversationPayload', payload);
          this.$store.commit('setUserContactInfo', this.userContactInfo);

          const chatWindowName = 'GeoChatWindow';
          const chatWindowUrl = this.$router.resolve({
            name: 'GeoChatView',
            query: {
              token: apiToken,
              apiUrl,
              socketIOUrl,
            },
          }).href;

          let chatWindow = window.open('', chatWindowName);

          if (!chatWindow || chatWindow.closed) {
            chatWindow = window.open(chatWindowUrl, chatWindowName);
          } else {
            chatWindow.location.href = chatWindowUrl;
            chatWindow.focus();
          }
          this.loading = false;
        } else {
          this.$notify.warn(
            this.$t('crm.chatCallerButton.messages.couldNotFoundAValidWaIdForNumber', {
              contactNumber: normalizedNumber,
            }),
            5000,
          );
        }
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loading = false;
    }
  }

  get showButton(): boolean {
    return !!this.conversationUserPermission;
  }

  get filterTooltipMessage(): string {
    return `${this.$t(this.tooltipDisabled)}`;
  }

  private normalizeContactNumber():string {
    const onlyNumbers = this.contactNumber.replace(/[^0-9]/g, '');

    const numbersWith55 = (onlyNumbers.length === 10 || onlyNumbers.length === 11)
      ? `55${onlyNumbers}`
      : onlyNumbers;

    if (numbersWith55.length === 13) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4');
    }

    if (numbersWith55.length === 12) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '+$1 ($2) $3-$4');
    }

    return numbersWith55;
  }

  private getClipboardContactNumber(): string {
    const onlyNumbers = this.contactNumber.replace(/[^0-9]/g, '');

    if ((onlyNumbers.length === 12 || onlyNumbers.length === 13) && onlyNumbers.startsWith('55')) {
      return onlyNumbers.substring(2);
    }

    return onlyNumbers;
  }

  private canOpenConversation(): boolean {
    if (!this.conversationUserPermission) {
      this.$notify.error(this.$t('crm.chatCallerButton.messages.errorOnGetPermissions'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.viewWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.whatsappDisabled'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.manageWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.youDoNotHavePermissiontoManageWhatsApp'), 5000);
      return false;
    }

    return true;
  }
}
